/**
 * IGES Exporter
 * Модуль для экспорта чертежей в формат IGES через Python backend
 */

import {
  getHoleDiameterForPipe,
  flangePipeInsertion,
  nakShaibaPipeMeasure,
  getShaibaKeyForFitting,
  PIPE_TO_HOLE_DIAMETER,
  DN_TO_PIPE_DIAMETER,
  NAK_GAIKA_TO_PIPE_DIAMETER,
  SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER,
  getMuftaAutoDisplayValue,
  getNippelAutoDisplayValue,
  getNippelMuftaInsertionOffset,
} from '../components/AutoConstructor/collectorUtils';
import fittingsData from '../components/fittingsData';
import fastenersData from '../components/fastenersData';
import { saveBlobAsFile } from './fileSystemSave';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const IGES_ACCEPT = { 'application/iges': ['.igs', '.iges'] };
const STEP_ACCEPT = { 'application/step': ['.stp', '.step'] };
const DXF_ACCEPT = { 'application/dxf': ['.dxf'] };

// Обратная таблица только для STEP: отверстие под сварку → наружный диаметр трубы.
const STEP_HOLE_TO_PIPE = new Map(
  Object.entries(PIPE_TO_HOLE_DIAMETER).map(([pipe, hole]) => [
    Math.round(Number(hole) * 10) / 10,
    Number(pipe),
  ]),
);

function stepRoundHoleDiameter(holeDiameter, fitting) {
  if (typeof holeDiameter === 'string' && holeDiameter.includes('x')) {
    return holeDiameter;
  }
  const kind = `${fitting?.type || ''} ${fitting?.id || ''}`.toLowerCase();
  if (kind.includes('mufta') || kind.includes('муфт')) {
    return holeDiameter;
  }
  const numeric = Number(holeDiameter);
  if (!Number.isFinite(numeric)) return holeDiameter;
  const key = Math.round(numeric * 10) / 10;
  if (STEP_HOLE_TO_PIPE.has(key)) return STEP_HOLE_TO_PIPE.get(key);
  // Размера не было в таблице. Раньше от 100 мм вычитали 0.5, теперь везде 0.3.
  const tenth = Math.round((key * 10) % 10);
  if (key >= 99.5 && tenth === 5) {
    return Math.round((key + 0.5) * 10) / 10;
  }
  const allowance = key < 29.8 ? 0.2 : 0.3;
  return Math.round((key + allowance) * 10) / 10;
}

const STEP_FITTING_KEY = /^(Nippel_|Mufta_|Zaglushka_|Bortshaiba_|Flanec_|Nak_Gaika_|Shaib_Nar_Rezba_|Shaib_Gaika_|Perehod_Konc_|Otvod_)/;

const ELBOW_BOX = {
  20: 51, 25: 55, 32: 70, 40: 81, 50: 105,
  65: 134, 80: 160, 100: 210, 125: 260, 150: 313,
};

const THREAD_BORE_BY_SUFFIX = {
  '1.4': 13.7,
  '3.8': 17.2,
  '1.2': 21.3,
  '3.4': 26.9,
  '1': 33.7,
  '1_1.4': 42.4,
  '1_1.2': 48.3,
  '2': 60.3,
};

function pipeThread(nominal) {
  const size = Number(nominal) || 0;
  if (size < 20) return { pitch: 1.337, depth: 0.85 };
  if (size < 30) return { pitch: 1.814, depth: 1.16 };
  return { pitch: 2.309, depth: 1.48 };
}

function pipeWall(outerDiameter) {
  if (outerDiameter < 30) return 2;
  if (outerDiameter < 90) return 2.5;
  return 3;
}

function boreFromKey(key, prefix) {
  if (!key.startsWith(prefix)) return null;
  return THREAD_BORE_BY_SUFFIX[key.slice(prefix.length)] || null;
}

function pipeFromDnKey(key, prefix) {
  const match = key.match(new RegExp(`^${prefix}(\\d+)`));
  if (!match) return null;
  return DN_TO_PIPE_DIAMETER[match[1]] || null;
}

function stubPipeInnerDiameter(key, prefix, table) {
  if (!key.startsWith(prefix)) return null;
  const outer = table[key.slice(prefix.length)];
  if (!outer) return null;
  return Math.max(4, outer - 6);
}

function stepFittingShape(key, width, height) {
  if (key.startsWith('Perehod_Konc_')) {
    const parts = key.slice('Perehod_Konc_'.length).split(/[xх×]/i);
    const large = Number(parts[0]);
    const small = Number(parts[1]);
    return {
      kind: 'reducer',
      smallDiameter: Number.isFinite(small) && small > 1 ? small : width * 0.8,
      largeDiameter: Number.isFinite(large) && large > 1 ? large : width,
    };
  }
  if (key === 'Otvod_25_32mm') {
    const pipe = 32;
    const box = 50;
    return {
      kind: 'elbow',
      pipeDiameter: pipe,
      bendRadius: Math.max(box - pipe / 2, pipe * 0.6),
    };
  }
  if (key.startsWith('Otvod_')) {
    const dn = Number(key.slice('Otvod_'.length));
    const pipe = DN_TO_PIPE_DIAMETER[String(dn)] || Math.min(width, height);
    const drawn = Math.max(width, height);
    const box = drawn > pipe * 1.3 ? drawn : (ELBOW_BOX[dn] || drawn);
    return {
      kind: 'elbow',
      pipeDiameter: pipe,
      bendRadius: Math.max(box - pipe / 2, pipe * 0.6),
    };
  }
  if (key.startsWith('Zaglushka_EL')) {
    return { kind: 'dome' };
  }
  if (key.startsWith('Zaglushka_')) {
    const square = key.match(/(\d+(?:[.,]\d+)?)\s*[xх×]\s*(\d+(?:[.,]\d+)?)/i);
    if (square) {
      return {
        kind: 'flat',
        square: true,
        sideWidth: Number(square[1].replace(',', '.')),
        sideHeight: Number(square[2].replace(',', '.')),
      };
    }
    return { kind: 'flat' };
  }
  if (key.startsWith('Nippel_')) {
    return {
      kind: 'tube',
      innerDiameter: Math.max(4, width - 2 * pipeWall(width)),
      thread: { side: 'external', from: 'tip', length: 15, diameter: width, ...pipeThread(width) },
    };
  }
  if (key.startsWith('Mufta_')) {
    const bore = boreFromKey(key, 'Mufta_') || width * 0.75;
    const hole = Math.min(bore, width - 2);
    return {
      kind: 'tube',
      innerDiameter: hole,
      thread: { side: 'internal', from: 'both', length: 15, diameter: hole, ...pipeThread(hole) },
    };
  }
  if (key.startsWith('Nak_Gaika_')) {
    const threadPipe = boreFromKey(key, 'Nak_Gaika_') || width * 0.7;
    const faceHole = Math.max(4, threadPipe - 6);
    return {
      kind: 'nut',
      innerDiameter: Math.min(threadPipe, width - 4),
      pipeBore: Math.min(faceHole, width - 4),
      backThickness: 2,
      thread: {
        side: 'internal',
        from: 'tip',
        zMin: 2.8,
        length: Math.max(4, height - 3.2),
        diameter: Math.min(threadPipe, width - 4),
        ...pipeThread(threadPipe),
      },
    };
  }
  if (key.startsWith('Shaib_Nar_Rezba_')) {
    const pipeInner = stubPipeInnerDiameter(key, 'Shaib_Nar_Rezba_', SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER);
    return {
      kind: 'tube',
      innerDiameter: Math.min(pipeInner || width * 0.6, width - 2),
      thread: { side: 'external', from: 'all', diameter: width, ...pipeThread(width) },
    };
  }
  if (key.startsWith('Shaib_Gaika_')) {
    return { kind: 'tube', innerDiameter: width * 0.62 };
  }
  if (key.startsWith('Bortshaiba_')) {
    const pipe = pipeFromDnKey(key, 'Bortshaiba_') || width * 0.55;
    const dn = Number((key.match(/^Bortshaiba_(\d+)/) || [])[1]);
    const face = dn === 125 ? 2.92 : (dn >= 150 ? 3 : 2);
    return {
      kind: 'collar',
      innerDiameter: Math.min(pipe, width - 4),
      faceThickness: face,
      filletRadius: Math.max(1, height - face),
    };
  }
  if (key.startsWith('Flanec_')) {
    const pipe = pipeFromDnKey(key, 'Flanec_') || width * 0.4;
    const welded = key.includes('welded');
    const inner = welded
      ? Math.max(4, pipe - 2 * pipeWall(pipe))
      : Math.min(pipe + 2, width - 4);
    return {
      kind: 'flange',
      innerDiameter: inner,
      hubDiameter: welded ? pipe * 1.45 : 0,
      hubLength: welded ? height * 0.4 : 0,
    };
  }
  return { kind: 'tube', innerDiameter: 0 };
}

// Сдвиг «вписать в лист» есть у живых объектов и отсутствует в снимке автоконструктора.
// Ручные трубы и отводы есть только на листе, поэтому без вычитания этого сдвига
// они попадают в STEP в стороне от остальной сборки.
let stepSheetShift = { dx: 0, dy: 0 };

function sheetFitShift(metadata, cylinders) {
  const snaps = metadata?.igesSnapshot?.cylinders || [];
  const buckets = new Map();
  snaps.forEach((snap) => {
    const live = (cylinders || []).find((item) => item.id === snap.id);
    if (!live || snap.x == null || live.x == null) return;
    const dx = Math.round((Number(live.x) - Number(snap.x)) * 10) / 10;
    const dy = Math.round((Number(live.y) - Number(snap.y)) * 10) / 10;
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;
    const key = `${dx}|${dy}`;
    const bucket = buckets.get(key) || { dx, dy, count: 0 };
    bucket.count += 1;
    buckets.set(key, bucket);
  });
  let best = { dx: 0, dy: 0, count: 0 };
  buckets.forEach((bucket) => {
    if (bucket.count > best.count) best = bucket;
  });
  return { dx: best.dx, dy: best.dy };
}

function groupPoint(localX, localY, item) {
  const groupX = Number(item.groupX) || 0;
  const groupY = Number(item.groupY) || 0;
  const offsetX = Number(item.offsetX) || 0;
  const offsetY = Number(item.offsetY) || 0;
  const rotation = ((Number(item.rotation) || 0) * Math.PI) / 180;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const dx = localX - offsetX;
  const dy = localY - offsetY;
  return {
    x: groupX + dx * cos - dy * sin,
    y: groupY + dx * sin + dy * cos,
  };
}

function cylinderDrawnOrigin(cyl) {
  return groupPoint(Number(cyl.x) || 0, Number(cyl.y) || 0, cyl);
}

function fittingPointOnSheet(fit, localX, localY) {
  return groupPoint((Number(fit.x) || 0) + localX, (Number(fit.y) || 0) + localY, fit);
}

function alignManualCylinder(cylinder, raw, metadata) {
  const snaps = metadata?.igesSnapshot?.cylinders || [];
  if (snaps.some((item) => item.id === cylinder.id && item.x != null)) return cylinder;
  const drawn = cylinderDrawnOrigin(raw || cylinder);
  const dx = drawn.x - (Number(raw?.x) || 0) - stepSheetShift.dx;
  const dy = drawn.y - (Number(raw?.y) || 0) - stepSheetShift.dy;
  if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return cylinder;
  return {
    ...cylinder,
    x: cylinder.x + dx,
    y: cylinder.y + dy,
    metadata: {
      ...cylinder.metadata,
      chartStartX: (cylinder.metadata?.chartStartX || 0) + dx,
      chartStartY: (cylinder.metadata?.chartStartY || 0) + dy,
    },
  };
}

function parentOuterRadius(fit, cylinders) {
  const parent = (cylinders || []).find((cylinder) => cylinder.id === fit.igesParentCylinderId);
  if (!parent) return 0;
  const width = Number(parent.width) || 0;
  const height = Number(parent.height) || 0;
  const cross = Math.min(width, height);
  return cross > 0 ? cross / 2 : 0;
}

function prepareStepFittingBodies(fittings, metadata, cylinders) {
  const snapshot = metadata?.igesSnapshot?.fittings || [];
  const bodies = [];
  (fittings || []).forEach((fit) => {
    if (fit.type === 'fastener' || fit.isHidden) return;
    const key = fit.key || fit.itemKey || '';
    if (!STEP_FITTING_KEY.test(key)) return;
    const width = Number(fit.width) || 0;
    const height = Number(fit.height) || 0;
    if (width < 0.5 || height < 0.5) return;

    const snap = snapshot.find((item) => item.id === fit.id);
    const placedByHand = !(snap && snap.groupX !== undefined);
    const groupX = placedByHand ? 0 : snap.groupX;
    const groupY = placedByHand ? 0 : (snap.groupY || 0);
    const rotation = placedByHand ? 0 : (Number(fit.rotation) || 0) * Math.PI / 180;
    const offsetX = placedByHand ? 0 : (Number(fit.offsetX) || width / 2);
    const offsetY = placedByHand ? 0 : (Number(fit.offsetY) || height);
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const toGlobal = (localX, localY) => {
      if (placedByHand) {
        const point = fittingPointOnSheet(fit, localX, localY);
        return { x: point.x - stepSheetShift.dx, y: point.y - stepSheetShift.dy };
      }
      const dx = localX - offsetX;
      const dy = localY - offsetY;
      return {
        x: groupX + dx * cos - dy * sin,
        y: groupY + dx * sin + dy * cos,
      };
    };
    const base = placedByHand ? toGlobal(width / 2, height) : toGlobal(offsetX, offsetY);
    const tip = placedByHand ? toGlobal(width / 2, 0) : toGlobal(offsetX, offsetY - height);
    const facingOut = fit.isSelfFacing || fit.isCenterFitting;
    const catalogAxial = Number(fittingsData[key]?.length) || 0;
    const faceDisk = facingOut && /^(Flanec_|Nak_Gaika_|Shaib_Nar_Rezba_)/.test(key) && catalogAxial >= 0.5;
    const shapeHeight = faceDisk ? catalogAxial : height;
    const shape = stepFittingShape(key, width, shapeHeight);
    const body = {
      id: fit.id,
      partKey: key,
      diameter: shape.largeDiameter || width,
      baseX: facingOut ? groupX : base.x,
      baseY: facingOut ? groupY : base.y,
      tipX: facingOut ? groupX : tip.x,
      tipY: facingOut ? groupY : tip.y,
      facing: facingOut ? (fit.side === 'back' ? 'back' : 'front') : null,
      length: facingOut ? shapeHeight : undefined,
      radialStart: facingOut ? parentOuterRadius(fit, cylinders) : 0,
      ...shape,
    };
    if (key === 'Mufta_1_1.4' && (fittings || []).some((stand) => {
      if (!stand.isFloorFastener || stand.isHidden) return false;
      const sx = (Number(stand.x) || 0) + (Number(stand.width) || 0) / 2;
      const top = Number(stand.y) || 0;
      const mx = (Number(fit.x) || 0) + width / 2;
      const bottom = (Number(fit.y) || 0) + height;
      return Math.abs(mx - sx) < 40 && Math.abs(bottom - top) < 12;
    })) {
      // На волосок уже Ø48,3, чтобы кромка не сливалась с трубой и не торчала пунктиром.
      body.diameter = 47.6;
    }
    if (shape.kind === 'elbow') {
      const pipe = shape.pipeDiameter;
      const bendR = shape.bendRadius;
      const arc = toGlobal(pipe / 2 + bendR, pipe / 2 + bendR);
      const down = toGlobal(pipe / 2, pipe / 2 + bendR);
      const right = toGlobal(pipe / 2 + bendR, pipe / 2);
      body.arcX = arc.x;
      body.arcY = arc.y;
      body.end1X = down.x;
      body.end1Y = down.y;
      body.end2X = right.x;
      body.end2Y = right.y;
    }
    bodies.push(body);
  });
  return bodies;
}

// STEP этих крепежей выгружен на максимальной высоте. Муфта 22 мм рисуется отдельно.
const STEP_FLOOR_FASTENER = {
  'KRNS-GS-03': 'KRNS-GS-03.STEP',
  'KRNS-GS-04': 'KRNS-GS-04.STEP',
  'Крепеж напольный нерегулируемый': 'KRNS-NR.STEP',
};
const FLOOR_FASTENER_MUFTA = 22;

function fastenerSheetPoint(fit, localX, localY) {
  const point = fittingPointOnSheet(fit, localX, localY);
  return {
    x: point.x - stepSheetShift.dx,
    y: point.y - stepSheetShift.dy,
  };
}

function prepareStepFasteners(fittings) {
  const bodies = [];
  (fittings || []).forEach((fit) => {
    if (fit.type !== 'fastener' || fit.isHidden) return;
    const key = fit.itemKey || fit.key || '';
    const file = STEP_FLOOR_FASTENER[key];
    const wallKey = key.startsWith('YKRS-') || key.startsWith('YKWK-');
    const generated = key === 'KRGB' || wallKey;
    if (!file && !generated) return;
    const width = Number(fit.width) || 0;
    const height = Number(fit.fastenerHeight || fit.height) || 0;
    if (width < 1 || height < 1) return;
    const spec = fastenersData[key] || {};
    const mufta = spec.noMufta ? 0 : FLOOR_FASTENER_MUFTA;
    const nominal = Math.max((Number(spec.maxHeight) || height) - mufta, height);
    const bottom = fastenerSheetPoint(fit, width / 2, height);
    const top = fastenerSheetPoint(fit, width / 2, 0);
    const right = fastenerSheetPoint(fit, width, height / 2);
    const left = fastenerSheetPoint(fit, 0, height / 2);
    bodies.push({
      id: fit.id,
      partKey: key,
      file,
      diameter: key === 'KRGB' ? (Number(fit.pipeDiameter || fit.width) || width) : undefined,
      axialDistance: wallKey ? (Number(fit.axialDistance) || 300) : undefined,
      clampDiameter: wallKey ? (Number(fit.clampDiameter) || spec.baseClampDiameter || 88.9) : undefined,
      overhang: wallKey ? (Number(fit.overhang) || 150) : undefined,
      height,
      rangeOffset: Number(fit.rangeOffset) || 0,
      nominalHeight: nominal,
      baseX: bottom.x,
      baseY: bottom.y,
      tipX: top.x,
      tipY: top.y,
      widthX: right.x - left.x,
      widthY: right.y - left.y,
    });
  });
  return bodies;
}

function outletVisibleLength(fit) {
  if (!fit?.overrideEnabled) return null;
  const value = Number(String(fit.overrideText).replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? value : null;
}

function cylinderSheetRect(cyl, metadata) {
  const snap = metadata?.igesSnapshot?.cylinders?.find((item) => item.id === cyl.id);
  if (snap && snap.x !== undefined) {
    return {
      id: cyl.id,
      x: snap.x,
      y: snap.y,
      width: snap.width !== undefined ? snap.width : cyl.width,
      height: snap.height !== undefined ? snap.height : cyl.height,
    };
  }
  const origin = cylinderDrawnOrigin(cyl);
  return {
    id: cyl.id,
    x: origin.x - stepSheetShift.dx,
    y: origin.y - stepSheetShift.dy,
    width: cyl.width,
    height: cyl.height,
  };
}

function stubTouchingOutletBase(base, rawCylinders, metadata) {
  let best = null;
  let bestGap = 4;
  (rawCylinders || []).forEach((cyl) => {
    const id = String(cyl.id || '');
    if (!id.includes('circuit') && !id.includes('intermediate')) return;
    const rect = cylinderSheetRect(cyl, metadata);
    const vertical = rect.height >= rect.width;
    let gap = Infinity;
    if (vertical) {
      const centerX = rect.x + rect.width / 2;
      if (Math.abs(centerX - base.x) > 4) return;
      gap = Math.min(Math.abs(base.y - rect.y), Math.abs(base.y - (rect.y + rect.height)));
    } else {
      const centerY = rect.y + rect.height / 2;
      if (Math.abs(centerY - base.y) > 4) return;
      gap = Math.min(Math.abs(base.x - rect.x), Math.abs(base.x - (rect.x + rect.width)));
    }
    if (gap < bestGap) {
      bestGap = gap;
      best = rect;
    }
  });
  return best;
}

function wallOppositeBase(stub, base) {
  const vertical = stub.height >= stub.width;
  if (vertical) {
    const top = stub.y;
    const bottom = stub.y + stub.height;
    return Math.abs(base.y - top) <= Math.abs(base.y - bottom)
      ? { x: base.x, y: bottom }
      : { x: base.x, y: top };
  }
  const left = stub.x;
  const right = stub.x + stub.width;
  return Math.abs(base.x - left) <= Math.abs(base.x - right)
    ? { x: right, y: base.y }
    : { x: left, y: base.y };
}

function shiftFittingBody(body, mx, my) {
  body.baseX += mx;
  body.baseY += my;
  body.tipX += mx;
  body.tipY += my;
  if (body.arcX != null) {
    body.arcX += mx;
    body.arcY += my;
    body.end1X += mx;
    body.end1Y += my;
    body.end2X += mx;
    body.end2Y += my;
  }
}

function addStickOutShift(shifts, id, mx, my) {
  if (!shifts || !id || (Math.abs(mx) < 1e-6 && Math.abs(my) < 1e-6)) return;
  const prev = shifts.get(id) || { mx: 0, my: 0 };
  shifts.set(id, { mx: prev.mx + mx, my: prev.my + my });
}

function stickOutFromDimensions(fitId, bodyLength, dimensionLines) {
  let best = null;
  (dimensionLines || []).forEach((dim) => {
    if (!dim?.overrideEnabled) return;
    const linked = (dim.elements || []).some((el) => el.objectId === fitId);
    if (!linked) return;
    const value = Number(String(dim.overrideText ?? '').replace(',', '.'));
    if (!Number.isFinite(value) || value <= 0 || value >= bodyLength - 0.2) return;
    if (best == null || value > best) best = value;
  });
  return best;
}

function sinkFacingOutlet(body, fit, rawCylinders) {
  const key = fit?.key || fit?.itemKey || '';
  if (!key.startsWith('Nippel_') && !key.startsWith('Mufta_')) return;
  const catalogLength = Number(fittingsData[key]?.length) || 0;
  if (catalogLength < 0.5) return;

  const parent = (rawCylinders || []).find((cyl) => cyl.id === fit.igesParentCylinderId);
  const shape = parent?.pipeSpec?.shape === 'square' ? 'square' : 'round';
  const pipeDiameter = Number(parent?.pipeSpec?.diameter) || 0;
  const autoVisible = key.startsWith('Nippel_')
    ? getNippelAutoDisplayValue(key, pipeDiameter, catalogLength, shape)
    : getMuftaAutoDisplayValue(key, catalogLength, shape);
  const typed = Number(fit?.fittingLength);
  const visible = outletVisibleLength(fit)
    ?? (Number.isFinite(typed) && typed > 0 ? typed : null)
    ?? autoVisible;
  if (visible == null) return;
  const offset = getNippelMuftaInsertionOffset(key, pipeDiameter, catalogLength, shape);
  const sink = Math.max(0, offset);
  const length = Number(visible) + sink;
  if (length < 0.5) return;

  body.length = length;
  body.wallRadius = Number(body.radialStart) || 0;
  if (sink > 0.2) {
    const radius = Number(body.radialStart) || 0;
    body.radialStart = Math.max(0, radius - sink);
  }
}

function positiveLength(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function facingPart(body, id, key, diameter, length, radialStart) {
  const shape = stepFittingShape(key, diameter, length);
  return {
    ...shape,
    id,
    partKey: key,
    diameter,
    baseX: body.baseX,
    baseY: body.baseY,
    tipX: body.tipX,
    tipY: body.tipY,
    facing: body.facing,
    radialStart,
    length,
  };
}

/**
 * Лицевой фланец, гайка и шайба с резьбой — тот же комплект, что у бокового выхода:
 * патрубок, бортшайба или шайба, фитинг. Вылет считается от наружной стенки.
 */
function expandFacingAssemblies(bodies, fittingsRaw, cylinders) {
  const rawById = new Map((fittingsRaw || []).map((fit) => [fit.id, fit]));
  const expanded = [];
  (bodies || []).forEach((body) => {
    if (body.facing !== 'front' && body.facing !== 'back') {
      expanded.push(body);
      return;
    }
    const fit = rawById.get(body.id);
    const key = fit?.key || fit?.itemKey || '';
    const parent = (cylinders || []).find((cyl) => cyl.id === fit?.igesParentCylinderId);
    const radius = parentOuterRadius(fit, cylinders);
    const shape = parent?.pipeSpec?.shape === 'square' ? 'square' : 'round';
    const mainDiameter = Number(parent?.pipeSpec?.diameter) || radius * 2;
    const user = positiveLength(fit?.flangePipeLength);
    const recess = shape === 'square' ? 3 : 10;

    if (key.startsWith('Flanec_')) {
      const dn = (key.match(/Flanec_(\d+)/) || [])[1];
      const pipeOd = DN_TO_PIPE_DIAMETER[dn];
      const info = fittingsData[key];
      if (!pipeOd || !info) {
        expanded.push(body);
        return;
      }
      const free = !key.endsWith('_welded');
      const collarInfo = free ? fittingsData[`Bortshaiba_${dn}`] : null;
      const collarLen = collarInfo?.length || 0;
      const visible = user ?? 100;
      const pipeShown = Math.max(0, visible - collarLen);
      const insertion = flangePipeInsertion(mainDiameter, pipeOd, shape);
      const wall = pipeWall(pipeOd);
      if (pipeShown + insertion > 0.5) {
        expanded.push(facingPart(
          body,
          `${body.id}-pipe`,
          'Nippel_1',
          pipeOd,
          pipeShown + insertion,
          Math.max(0, radius - insertion),
        ));
        const pipe = expanded[expanded.length - 1];
        pipe.innerDiameter = Math.max(1, pipeOd - 2 * wall);
        pipe.thread = null;
        pipe.wallRadius = radius;
        pipe.roundWall = shape !== 'square';
        pipe.wallAxis = shape !== 'square' && (Number(parent?.height) || 0) > (Number(parent?.width) || 0) ? 'y' : 'x';
      }
      if (collarInfo && collarLen > 0.5) {
        expanded.push(facingPart(
          body,
          `${body.id}-collar`,
          `Bortshaiba_${dn}`,
          collarInfo.diameter,
          collarLen,
          radius + pipeShown,
        ));
      }
      const flangeLen = info.length;
      expanded.push(facingPart(
        body,
        body.id,
        key,
        info.diameter,
        flangeLen,
        Math.max(0, radius + pipeShown - flangeLen),
      ));
      return;
    }

    if (key.startsWith('Nak_Gaika_')) {
      const size = key.slice('Nak_Gaika_'.length);
      const pipeOd = NAK_GAIKA_TO_PIPE_DIAMETER[size];
      const washerKey = getShaibaKeyForFitting(key);
      const washer = fittingsData[washerKey];
      const nut = fittingsData[key];
      if (!pipeOd || !washer || !nut) {
        expanded.push(body);
        return;
      }
      const measure = nakShaibaPipeMeasure(user, washer.length, shape, false);
      const washerLen = washer.length || 0;
      const pipeShown = measure.outside;
      const inner = Math.max(1, pipeOd - 6);
      expanded.push(facingPart(
        body,
        `${body.id}-pipe`,
        'Nippel_1',
        pipeOd,
        pipeShown + recess,
        Math.max(0, radius - recess),
      ));
      const pipe = expanded[expanded.length - 1];
      pipe.innerDiameter = inner;
      pipe.thread = null;
      pipe.wallRadius = radius;
      pipe.roundWall = shape !== 'square';
      pipe.wallAxis = shape !== 'square' && (Number(parent?.height) || 0) > (Number(parent?.width) || 0) ? 'y' : 'x';
      expanded.push(facingPart(
        body,
        `${body.id}-washer`,
        washerKey,
        washer.diameter,
        washerLen,
        radius + pipeShown,
      ));
      const nutLen = nut.length;
      expanded.push(facingPart(
        body,
        body.id,
        key,
        nut.diameter,
        nutLen,
        Math.max(0, radius + pipeShown - nutLen),
      ));
      return;
    }

    if (key.startsWith('Shaib_Nar_Rezba_')) {
      const size = key.slice('Shaib_Nar_Rezba_'.length);
      const pipeOd = SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER[size];
      const washer = fittingsData[key];
      if (!pipeOd || !washer) {
        expanded.push(body);
        return;
      }
      const measure = nakShaibaPipeMeasure(user, washer.length, shape, true);
      const washerLen = washer.length || 10;
      const pipeShown = measure.outside;
      expanded.push(facingPart(
        body,
        `${body.id}-pipe`,
        'Nippel_1',
        pipeOd,
        pipeShown + measure.insertion,
        Math.max(0, radius - recess),
      ));
      const pipe = expanded[expanded.length - 1];
      pipe.innerDiameter = Math.max(1, pipeOd - 6);
      pipe.thread = null;
      pipe.roundWall = shape !== 'square';
      pipe.wallAxis = shape !== 'square' && (Number(parent?.height) || 0) > (Number(parent?.width) || 0) ? 'y' : 'x';
      expanded.push(facingPart(
        body,
        body.id,
        key,
        washer.diameter,
        washerLen,
        radius + pipeShown,
      ));
      return;
    }

    if (shape !== 'square') {
      body.roundWall = true;
      body.wallAxis = (Number(parent?.height) || 0) > (Number(parent?.width) || 0) ? 'y' : 'x';
    }
    expanded.push(body);
  });
  bodies.splice(0, bodies.length, ...expanded);
}

function shiftPreparedCylinder(cylinders, id, mx, my) {
  const cylinder = (cylinders || []).find((item) => item.id === id);
  if (!cylinder) return;
  const meta = cylinder.metadata || {};
  cylinder.metadata = meta;
  meta.chartStartX = (Number(meta.chartStartX) || 0) + mx;
  meta.chartStartY = (Number(meta.chartStartY) || 0) + my;
  if (cylinder.orientation !== 'vertical') {
    cylinder.y = (Number(cylinder.y) || 0) + my;
  }
}

/**
 * «Изменённое значение» — вылет от наружной стенки.
 * Тело на чертеже длиннее: разницу задвигаем в отверстие, наружный конец остаётся на этом вылете.
 */
function isEndConnectionFitting(fit) {
  const id = String(fit?.id || '');
  if (id.includes('-conn-')) return true;
  // Торцы гидроразделителя: сверху и снизу, вместе с шайбой. Контуры снизу не трогаем.
  if (id.startsWith('fitting-bottom-circuit-')) return false;
  if (id.startsWith('fitting-top-') || id.startsWith('fitting-bottom-')) return true;
  const parts = id.split('-');
  if (parts[0] === 'fitting' && (parts[1] === 'left' || parts[1] === 'right') && parts.length === 3) {
    const stamp = Number(parts[2]);
    return stamp > 10000000000;
  }
  return false;
}

function sinkStepOutlets(fittingBodies, fittingsRaw, preparedCylinders, rawCylinders, metadata, dimensionLines, shifts) {
  const rawById = new Map((fittingsRaw || []).map((fit) => [fit.id, fit]));
  const movedBodies = new Set();
  const movedCylinders = new Set();

  (fittingBodies || []).forEach((body) => {
    if (movedBodies.has(body.id)) return;
    const fit = rawById.get(body.id);
    if (isEndConnectionFitting(fit)) return;
    if (body.facing) {
      sinkFacingOutlet(body, fit, rawCylinders);
      movedBodies.add(body.id);
      return;
    }
    const key = fit?.key || fit?.itemKey || '';
    if (key.startsWith('Shaib_') || key.startsWith('Nak_Gaika_')) return;
    const measuresOutlet = key.startsWith('Nippel_') || key.startsWith('Mufta_');
    if (!measuresOutlet) return;

    const base = { x: body.baseX, y: body.baseY };
    const tip = { x: body.tipX, y: body.tipY };
    const ownLength = Math.hypot(tip.x - base.x, tip.y - base.y);
    if (ownLength < 0.5) return;

    const labeled = outletVisibleLength(fit)
      ?? stickOutFromDimensions(fit.id, ownLength, dimensionLines);
    const catalogLength = Number(fittingsData[key]?.length) || 0;
    const parent = (rawCylinders || []).find((cyl) => cyl.id === fit?.igesParentCylinderId);
    const shape = parent?.pipeSpec?.shape === 'square' ? 'square' : 'round';
    const pipeDiameter = Number(parent?.pipeSpec?.diameter) || 0;
    const offset = getNippelMuftaInsertionOffset(key, pipeDiameter, catalogLength, shape);

    // Старый чертёж: тело длиннее подписи, торец задвигаем до видимого вылета.
    if (labeled != null && ownLength > labeled + 0.5) {
      const visible = labeled;
      const directFitting = key.startsWith('Nippel_') || key.startsWith('Mufta_');
      const stub = directFitting ? null : stubTouchingOutletBase(base, rawCylinders, metadata);
      const wall = stub ? wallOppositeBase(stub, base) : base;
      const span = Math.hypot(tip.x - wall.x, tip.y - wall.y);
      const sink = span - visible;
      if (sink < 0.2 || sink > 40) return;

      const mx = ((wall.x - tip.x) / span) * sink;
      const my = ((wall.y - tip.y) / span) * sink;
      shiftFittingBody(body, mx, my);
      addStickOutShift(shifts, body.id, mx, my);
      movedBodies.add(body.id);

      if (!stub) return;
      const stubId = String(stub.id || '');
      const stubEntersByLength = stubId.includes('nakgaika') || stubId.includes('cyl-circuit-shaiba');
      if (!stubEntersByLength && !movedCylinders.has(stub.id)) {
        shiftPreparedCylinder(preparedCylinders, stub.id, mx, my);
        addStickOutShift(shifts, stub.id, mx, my);
        movedCylinders.add(stub.id);
      }
      (fittingBodies || []).forEach((other) => {
        if (movedBodies.has(other.id) || other.facing) return;
        const otherKey = rawById.get(other.id)?.key || rawById.get(other.id)?.itemKey || '';
        if (!otherKey.startsWith('Nak_Gaika_')) return;
        const along = (other.baseX - wall.x) * (tip.x - wall.x) + (other.baseY - wall.y) * (tip.y - wall.y);
        const cross = Math.abs((other.baseX - wall.x) * (tip.y - wall.y) - (other.baseY - wall.y) * (tip.x - wall.x));
        if (along < -span || cross > span * 4) return;
        shiftFittingBody(other, mx, my);
        addStickOutShift(shifts, other.id, mx, my);
        movedBodies.add(other.id);
      });
      return;
    }

    // Новый чертёж: высота тела уже равна видимому вылету. Заход добавляем внутрь трубы, торец не двигаем.
    if (offset > 0.2) {
      const ux = (base.x - tip.x) / ownLength;
      const uy = (base.y - tip.y) / ownLength;
      body.baseX += ux * offset;
      body.baseY += uy * offset;
      movedBodies.add(body.id);
    }
  });
  return movedCylinders;
}

/**
 * Раньше выход сдвигался внутрь на 8/4 мм и видимый торец уезжал.
 * Заход теперь в insertionDepth, торец остаётся на вылете.
 */
function pushNakShaibaOutletsToChangedValue(cylinders, rawCylinders, fittingBodies, fittingsRaw, welds, metadata, shifts) {
  const rawById = new Map((fittingsRaw || []).map((fit) => [fit.id, fit]));
  const mains = (rawCylinders || []).filter((item) => {
    const id = String(item.id || '');
    return !id.includes('circuit') && !id.includes('intermediate');
  });

  const shifted = new Set();
  const isOutletFitting = (body) => {
    const key = rawById.get(body.id)?.key || rawById.get(body.id)?.itemKey || '';
    return key.startsWith('Nak_Gaika_') || key.startsWith('Shaib_Gaika_') || key.startsWith('Shaib_Nar_Rezba_');
  };
  const fittingOnEnd = (endX, endY) => (fittingBodies || []).some((body) => (
    isOutletFitting(body) && (
      Math.hypot(body.baseX - endX, body.baseY - endY) < 40
      || Math.hypot(body.tipX - endX, body.tipY - endY) < 40
    )
  ));

  cylinders.forEach((cylinder) => {
    const id = String(cylinder.id || '');
    if (!id.includes('nakgaika') && !id.includes('cyl-circuit-shaiba')) return;
    const raw = (rawCylinders || []).find((item) => item.id === cylinder.id) || cylinder;
    const rect = cylinderSheetRect(raw, metadata);
    const vertical = cylinder.orientation === 'vertical';
    const centerX = rect.x + rect.width / 2;
    const centerY = rect.y + rect.height / 2;

    let dx = 0;
    let dy = 0;
    let square = false;
    if (vertical) {
      const top = rect.y;
      const bottom = rect.y + rect.height;
      const freeAtBottom = fittingOnEnd(centerX, bottom) && !fittingOnEnd(centerX, top);
      const rootY = freeAtBottom ? top : bottom;
      let mainCenter = null;
      let best = Infinity;
      mains.forEach((main) => {
        const box = cylinderSheetRect(main, metadata);
        if (centerX < box.x - 2 || centerX > box.x + box.width + 2) return;
        const center = box.y + box.height / 2;
        const dist = Math.abs(rootY - center);
        if (dist < best) {
          best = dist;
          mainCenter = center;
          square = main.pipeSpec?.shape === 'square';
        }
      });
      const toward = mainCenter == null ? (freeAtBottom ? -1 : 1) : (rootY > mainCenter ? -1 : 1);
      dy = 0 * toward;
    } else {
      const left = rect.x;
      const right = rect.x + rect.width;
      const freeAtRight = fittingOnEnd(right, centerY) && !fittingOnEnd(left, centerY);
      const rootX = freeAtRight ? left : right;
      let mainCenter = null;
      let best = Infinity;
      mains.forEach((main) => {
        const box = cylinderSheetRect(main, metadata);
        if (centerY < box.y - 2 || centerY > box.y + box.height + 2) return;
        const center = box.x + box.width / 2;
        const dist = Math.abs(rootX - center);
        if (dist < best) {
          best = dist;
          mainCenter = center;
          square = main.pipeSpec?.shape === 'square';
        }
      });
      const toward = mainCenter == null ? (freeAtRight ? -1 : 1) : (rootX > mainCenter ? -1 : 1);
      dx = 0 * toward;
    }
    void square;

    const meta = cylinder.metadata || {};
    cylinder.metadata = meta;
    if (vertical) meta.chartStartY = (Number(meta.chartStartY) || rect.y) + dy;
    else meta.chartStartX = (Number(meta.chartStartX) || rect.x) + dx;
    if (!vertical) cylinder.y = (Number(cylinder.y) || 0) + dy;
    addStickOutShift(shifts, cylinder.id, dx, dy);

    (fittingBodies || []).forEach((body) => {
      if (shifted.has(body.id) || !isOutletFitting(body)) return;
      if (isEndConnectionFitting(rawById.get(body.id))) return;
      if (vertical) {
        if (Math.abs(body.baseX - centerX) > 4) return;
      } else if (Math.abs(body.baseY - centerY) > 4) return;
      shiftFittingBody(body, dx, dy);
      addStickOutShift(shifts, body.id, dx, dy);
      shifted.add(body.id);
    });
  });
}

function weldObjectIds(weld) {
  if (Array.isArray(weld?.objects) && weld.objects.length >= 2) {
    return [weld.objects[0], weld.objects[1]];
  }
  const first = weld?.obj1?.objectId;
  const second = weld?.obj2?.objectId;
  return first && second ? [first, second] : [];
}

/**
 * Отвод или труба, поставленные вручную, сдвигаются на ту же величину,
 * что и приваренный к ним патрубок после изменённого значения.
 * Дальше сдвиг идёт по цепочке швов, но не на трубы автоконструктора.
 */
function followWeldedStickOut(cylinders, fittingBodies, fittingsRaw, welds, metadata, shifts) {
  if (!shifts || shifts.size === 0) return;
  const snapshotIds = new Set((metadata?.igesSnapshot?.cylinders || []).map((item) => item.id));
  const rawById = new Map((fittingsRaw || []).map((fit) => [fit.id, fit]));
  const bodyById = new Map((fittingBodies || []).map((body) => [body.id, body]));
  const movable = new Set();
  (cylinders || []).forEach((cylinder) => {
    if (!snapshotIds.has(cylinder.id)) movable.add(cylinder.id);
  });
  (fittingBodies || []).forEach((body) => {
    const raw = rawById.get(body.id);
    const key = raw?.key || raw?.itemKey || '';
    if (key.startsWith('Otvod_')) movable.add(body.id);
  });

  const neighbors = new Map();
  const link = (first, second) => {
    if (!first || !second || first === second) return;
    if (!neighbors.has(first)) neighbors.set(first, new Set());
    if (!neighbors.has(second)) neighbors.set(second, new Set());
    neighbors.get(first).add(second);
    neighbors.get(second).add(first);
  };
  (welds || []).forEach((weld) => {
    const [first, second] = weldObjectIds(weld);
    link(first, second);
  });

  const queue = [...shifts.keys()];
  const seen = new Set(queue);
  while (queue.length > 0) {
    const id = queue.shift();
    const delta = shifts.get(id);
    if (!delta) continue;
    (neighbors.get(id) || []).forEach((next) => {
      if (seen.has(next) || !movable.has(next)) return;
      seen.add(next);
      shifts.set(next, { mx: delta.mx, my: delta.my });
      const body = bodyById.get(next);
      if (body) shiftFittingBody(body, delta.mx, delta.my);
      else shiftPreparedCylinder(cylinders, next, delta.mx, delta.my);
      queue.push(next);
    });
  }
}

function pointToRect(point, box) {
  const x = Math.min(Math.max(point.x, box.x), box.x + box.width);
  const y = Math.min(Math.max(point.y, box.y), box.y + box.height);
  return Math.hypot(point.x - x, point.y - y);
}

/**
 * Глубина врезки добавляется к тому концу, который упирается в другую трубу.
 * У подключений шва на чертеже нет, поэтому по сварке конец выбирался неверно
 * и труба удлинялась наружу.
 */
function markInsertionAtTouchingEnd(cylinders, rawCylinders, metadata) {
  (cylinders || []).forEach((cylinder) => {
    const depth = Number(cylinder.insertionDepth) || 0;
    if (depth <= 0) return;
    const raw = (rawCylinders || []).find((item) => item.id === cylinder.id) || cylinder;
    const rect = cylinderSheetRect(raw, metadata);
    const vertical = cylinder.orientation === 'vertical';
    const start = vertical
      ? { x: rect.x + rect.width / 2, y: rect.y }
      : { x: rect.x, y: rect.y + rect.height / 2 };
    const end = vertical
      ? { x: start.x, y: rect.y + rect.height }
      : { x: rect.x + rect.width, y: start.y };
    let startGap = Infinity;
    let endGap = Infinity;
    (rawCylinders || []).forEach((other) => {
      if (other.id === cylinder.id) return;
      const box = cylinderSheetRect(other, metadata);
      startGap = Math.min(startGap, pointToRect(start, box));
      endGap = Math.min(endGap, pointToRect(end, box));
    });
    cylinder.insertionAtStart = startGap <= endGap;
  });
}

function resolveInsertionEnd(cyl, isHorizontal, cylX, cylY, cylWidth, cylHeight, welds) {
  const depth = Number(cyl.insertionDepth) || 0;
  if (depth <= 0) return 'start';
  const points = (welds || []).filter((weld) => (
    (weld.objects && weld.objects.includes(cyl.id))
    || weld.obj1?.objectId === cyl.id
    || weld.obj2?.objectId === cyl.id
  ));
  if (points.length === 0) return 'start';
  if (isHorizontal) {
    const weldX = points.reduce((sum, weld) => sum + (Number(weld.x) || 0), 0) / points.length;
    const end = cylX + cylWidth;
    return Math.abs(weldX - cylX) <= Math.abs(weldX - end) ? 'start' : 'end';
  }
  const weldY = points.reduce((sum, weld) => sum + (Number(weld.y) || 0), 0) / points.length;
  const end = cylY + cylHeight;
  return Math.abs(weldY - cylY) <= Math.abs(weldY - end) ? 'start' : 'end';
}

function filterMainCylinders(cylinders) {
  return cylinders.filter((c) =>
    !c.id?.includes('intermediate')
    && !c.id?.startsWith('cyl-circuit-')
    && !c.id?.startsWith('cyl-bottom-circuit-')
    && !c.id?.startsWith('cyl-left-')
    && !c.id?.startsWith('cyl-right-')
    && !c.id?.startsWith('cyl-top-')
    && !c.id?.startsWith('cyl-bottom-')
    && !c.id?.includes('-connector-'),
  );
}

/**
 * Синхронный план сохранения IGES (для диалога до backend).
 * @returns {{ mode: 'single', suggestedName: string } | { mode: 'multi' } | { mode: 'error', message: string }}
 */
export function getIgesExportSavePlan(drawingData) {
  if (!drawingData) {
    return { mode: 'error', message: 'Нет данных для экспорта' };
  }
  const welds = drawingData.welds || [];
  const rawCylinders = drawingData.cylinders || [];
  const metadata = drawingData.metadata || {};
  const cylinders = prepareCylindersData(rawCylinders, welds, metadata);
  if (cylinders.length === 0) {
    return { mode: 'error', message: 'Нет труб для экспорта' };
  }
  const mainCylinders = filterMainCylinders(cylinders);
  if (mainCylinders.length === 1) {
    const suggestedName = generateFilename(drawingData, mainCylinders[0], null);
    return { mode: 'single', suggestedName };
  }
  if (mainCylinders.length === 0) {
    return { mode: 'error', message: 'Нет основных труб для экспорта' };
  }
  return { mode: 'multi' };
}

/**
 * Проверяет доступность backend сервера
 * @returns {Promise<boolean>}
 */
export async function checkBackendHealth() {
  try {
    const response = await fetch(`${BACKEND_URL}/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      return false;
    }
    
    const data = await response.json();
    return data.status === 'ok' && data.pythonocc_available;
    
  } catch (error) {
    console.error('Backend недоступен:', error);
    return false;
  }
}

/**
 * Преобразует данные цилиндров из чертежа в формат для backend
 * @param {Array} cylinders - массив цилиндров из Canvas
 * @param {Array} welds - массив сварных швов для определения отверстий
 * @param {Object} metadata - метаданные чертежа (включая igesSnapshot)
 * @returns {Array}
 */
function prepareCylindersData(cylinders, welds = [], metadata = {}) {
  if (!cylinders || !Array.isArray(cylinders)) {
    return [];
  }
  
  // IGES: Если есть снимок координат, используем его вместо текущих координат
  const igesSnapshot = metadata.igesSnapshot;
  
  return cylinders.map(cyl => {
    // IGES: Получаем сохранённые координаты из снимка (если есть)
    let snapshotData = null;
    if (igesSnapshot && igesSnapshot.cylinders) {
      snapshotData = igesSnapshot.cylinders.find(s => s.id === cyl.id);
    }
    
    // Используем снимок если есть, иначе текущие координаты
    const cylX = snapshotData ? snapshotData.x : cyl.x;
    const cylY = snapshotData ? snapshotData.y : cyl.y;
    const cylWidth = snapshotData ? snapshotData.width : cyl.width;
    const cylHeight = snapshotData ? snapshotData.height : cyl.height;
    const cylRotation = snapshotData ? snapshotData.rotation : cyl.rotation;
    
    // Проверяем форму трубы (круглая или квадратная)
    const isSquare = cyl.pipeSpec && cyl.pipeSpec.shape === 'square';
    
    // Ось трубы — та сторона прямоугольника, которая не равна диаметру.
    // Короткий патрубок может быть ниже своего диаметра, и сравнения width > height недостаточно.
    const roundDiameter = !isSquare ? Number(cyl.pipeSpec?.diameter) || 0 : 0;
    const isHorizontal = roundDiameter > 0
      ? Math.abs(cylHeight - roundDiameter) <= Math.abs(cylWidth - roundDiameter)
      : cylWidth > cylHeight;
    
    const length = isHorizontal ? cylWidth : cylHeight;
    
    // Получаем толщину стенки из pipeSpec
    let thickness = 3; // значение по умолчанию
    if (cyl.pipeSpec && cyl.pipeSpec.thickness) {
      const thicknessStr = String(cyl.pipeSpec.thickness);
      // Парсим строку типа "x2", "x3" или просто число
      if (thicknessStr.startsWith('x')) {
        thickness = Number(thicknessStr.substring(1));
      } else {
        thickness = Number(thicknessStr);
      }
      
      // Проверка на NaN
      if (isNaN(thickness) || thickness <= 0) {
        thickness = 3;
      }
    } else {
      thickness = 3;
    }
    
    let outerDiameter, innerDiameter, outerWidth, outerHeight, innerWidth, innerHeight;
    
    if (isSquare) {
      // Для квадратного профиля парсим размеры из profile (например, "20x20x2" или "60x30x2")
      const profile = cyl.pipeSpec.profile || '20x20x2';
      const parts = profile.split('x').map(p => Number(p));
      
      if (parts.length >= 2) {
        const size1 = parts[0];
        const size2 = parts[1];
        
        // ВАЖНО: Для компактного режима (прямоугольные профили типа 60x30 или 80x40)
        // нужно, чтобы БОЛЬШИЙ размер был по высоте (Z), а МЕНЬШИЙ по ширине (Y)
        // В profile записано "60x30", где 60 > 30, поэтому:
        // outerWidth (Y) = меньший размер
        // outerHeight (Z) = больший размер
        if (size1 !== size2) {
          // Прямоугольный профиль - разворачиваем так, чтобы больший был по Z
          outerWidth = Math.min(size1, size2);   // Меньший размер по Y (ширина)
          outerHeight = Math.max(size1, size2);  // Больший размер по Z (высота)
        } else {
          // Квадратный профиль - размеры одинаковые
          outerWidth = size1;
          outerHeight = size2;
        }
        
        // Толщина может быть в profile или в thickness
        if (parts.length >= 3) {
          thickness = parts[2];
        }
      } else {
        // Fallback: используем height как размер квадрата
        const size = isHorizontal ? cylHeight : cylWidth;
        outerWidth = size;
        outerHeight = size;
      }
      
      innerWidth = outerWidth - 2 * thickness;
      innerHeight = outerHeight - 2 * thickness;
      
    } else {
      // Для круглой трубы
      outerDiameter = isHorizontal ? cylHeight : cylWidth;
      innerDiameter = outerDiameter - 2 * thickness;
    }
    
    // Центр по вертикали листа — середина нарисованного прямоугольника.
    // У 80×40 высота на листе 40, а не большая сторона профиля.
    const drawnCross = isHorizontal ? cylHeight : cylWidth;
    const pipeSize = isSquare ? drawnCross : outerDiameter;
    const centerY = cylY + (isHorizontal ? pipeSize : length) / 2;
    
    // Находим все сварные швы, связанные с этим цилиндром
    const cylinderWelds = welds.filter(weld => {
      // Проверяем оба формата: старый (objects) и новый (obj1/obj2)
      if (weld.objects && weld.objects.includes(cyl.id)) {
        return true;
      }
      if (weld.obj1 && weld.obj1.objectId === cyl.id) {
        return true;
      }
      if (weld.obj2 && weld.obj2.objectId === cyl.id) {
        return true;
      }
      return false;
    });
    
    // Собираем информацию об отверстиях из сварных швов
    const holes = cylinderWelds.map(weld => {
      // Координаты сварного шва - это позиция отверстия на трубе
      const holeX = weld.x;
      const holeY = weld.y;
      
      // Вычисляем расстояние вдоль трубы (от начала цилиндра)
      // Для вертикальных труб: расстояние = holeY - cyl.y
      // Для горизонтальных: расстояние = holeX - cyl.x
      const distanceAlongPipe = Math.abs(holeY - cyl.y);
      
      // Определяем направление отверстия (радиальное = перпендикулярно оси трубы)
      // Для вертикальных труб отверстия обычно горизонтальные
      const isVertical = cyl.height > cyl.width;
      
      return {
        x: holeX,
        y: holeY,
        distanceAlongPipe: distanceAlongPipe,
        // Диаметр отверстия будет определен позже из связанного фитинга
        diameter: 20, // значение по умолчанию
        direction: isVertical ? 'horizontal' : 'vertical',
      };
    });
    
    const result = {
      id: cyl.id,
      x: cylX,  // Начало трубы на чертеже (из снимка или текущее)
      y: centerY,
      z: cyl.z || 0,
      thickness: thickness,
      length: length,
      angle: cylRotation || cyl.angle || 0,
      holes: holes,
      // Ориентация трубы: 'horizontal' (по X) или 'vertical' (по Y)
      orientation: isHorizontal ? 'horizontal' : 'vertical',
      // Глубина вставки в гидроразделитель (для круглых труб коллектора)
      insertionDepth: cyl.insertionDepth || 0,
      // К какому концу прямоугольника на листе прибавлять вставку: к сварному шву
      insertionEnd: resolveInsertionEnd(cyl, isHorizontal, cylX, cylY, cylWidth, cylHeight, welds),
      // Ширина прямоугольника на листе: для вертикальной трубы это диаметр
      bboxWidth: cylWidth,
      // Покрытие из актуального pipeSpec (для имени файла)
      coating: cyl.pipeSpec?.coating || 'Матовый',
      // Добавляем метаданные для расчета позиций отверстий
      metadata: {
        chartStartX: cylX,  // Начало трубы на 2D чертеже (из снимка)
        chartStartY: cylY,  // Начало трубы на 2D чертеже (из снимка)
        // Параметры контуров из автоконструктора
        circuitParams: metadata.circuitParams || null,
        // Параметры для распределительного коллектора (подающий и обратный)
        supplyCircuitParams: metadata.supplyCircuitParams || null,
        returnCircuitParams: metadata.returnCircuitParams || null,
        // Параметры для предкотлового коллектора (подающий и обратный)
        preBoilerSupplyCircuitParams: metadata.preBoilerSupplyCircuitParams || null,
        preBoilerReturnCircuitParams: metadata.preBoilerReturnCircuitParams || null,
      },
    };
    
    // Добавляем параметры в зависимости от формы
    if (isSquare) {
      result.shape = 'square';
      result.outerWidth = outerWidth;
      result.outerHeight = outerHeight;
      result.innerWidth = innerWidth;
      result.innerHeight = innerHeight;
      if (cyl.pipeSpec?.squareType === 'sheet') {
        result.section = 'channel';
      }
    } else {
      result.shape = 'round';
      result.outerDiameter = outerDiameter;
      result.innerDiameter = innerDiameter;
    }
    
    return result;
  });
}

/**
 * Преобразует данные фитингов из чертежа в формат для backend
 * @param {Array} fittings - массив фитингов из Canvas
 * @param {Array} cylinders - массив цилиндров для определения связей
 * @param {Array} welds - массив сварных швов для определения связей
 * @param {Object} metadata - метаданные чертежа (включая igesSnapshot)
 * @returns {Array}
 */
function prepareFittingsData(fittings, cylinders, welds = [], metadata = {}) {
  if (!fittings || !Array.isArray(fittings)) {
    return [];
  }
  
  // IGES: Если есть снимок координат, используем его вместо текущих координат
  const igesSnapshot = metadata.igesSnapshot;
  
  return fittings.filter(fit => {
    // Исключаем крепежи — они не создают отверстий в IGES
    if (fit.type === 'fastener') {
      return false;
    }
    
    // Исключаем торцевые фитинги (заглушки, торцевые подключения) — они не создают отверстий
    if (fit.id?.startsWith('fitting-cap-') || 
        fit.id?.startsWith('fitting-top-') || 
        fit.id?.startsWith('fitting-bottom-') ||
        fit.id?.startsWith('fitting-shaiba-left-') ||   // шайбы торцевых этажного коллектора
        fit.id?.startsWith('fitting-shaiba-right-')) {  // шайбы торцевых этажного коллектора
      // Но НЕ исключаем fitting-bottom-circuit- (нижние контуры этажного коллектора)
      if (fit.id?.startsWith('fitting-bottom-circuit-')) {
        return true; // оставляем
      }
      return false; // исключаем торцевые
    }
    
    // Исключаем ТОЛЬКО торцевые подключения этажного коллектора (с длинным timestamp в ID)
    // Но НЕ исключаем боковые подключения гидроразделителя (короткий индекс)
    // Примеры:
    //   - fitting-left-1737472839123 (этажный, timestamp ~13 цифр) - ИСКЛЮЧИТЬ
    //   - fitting-left-0-1737472839123 (гидроразделитель, индекс-timestamp) - ОСТАВИТЬ
    if (fit.id?.startsWith('fitting-left-') || fit.id?.startsWith('fitting-right-')) {
      // Разбираем ID: fitting-left-XXX или fitting-left-X-YYY
      const parts = fit.id.split('-');
      // parts = ['fitting', 'left'/'right', 'XXX' или 'X', 'YYY'?]
      
      if (parts.length >= 3) {
        const thirdPart = parts[2]; // это либо timestamp, либо индекс
        
        // Если третья часть - длинное число (timestamp > 10000000000, т.е. > 10 млрд)
        // то это торцевое подключение этажного коллектора
        const numberValue = parseInt(thirdPart, 10);
        if (!isNaN(numberValue) && numberValue > 10000000000) {
          return false; // исключаем торцевые этажного
        }
      }
      
      // Иначе это боковое подключение гидроразделителя - оставляем
      return true;
    }
    
    return true;
  }).map(fit => {
    // IGES: Получаем сохранённые координаты и параметры из снимка (если есть)
    let snapshotData = null;
    if (igesSnapshot && igesSnapshot.fittings) {
      snapshotData = igesSnapshot.fittings.find(s => s.id === fit.id);
    }
    
    // Используем снимок если есть, иначе текущие данные
    const fitGroupX = snapshotData ? snapshotData.groupX : fit.groupX;
    const fitGroupY = snapshotData ? snapshotData.groupY : fit.groupY;
    const fitDiameter = snapshotData ? snapshotData.diameter : fit.diameter;
    
    // IGES параметры также из снимка (если есть)
    const igesParentCylinderId = snapshotData ? snapshotData.igesParentCylinderId : fit.igesParentCylinderId;
    const igesCreateHole = snapshotData ? snapshotData.igesCreateHole : fit.igesCreateHole;
    const holeDiameter = snapshotData ? snapshotData.holeDiameter : fit.holeDiameter;
    const igesHoleDirection = snapshotData ? snapshotData.igesHoleDirection : fit.igesHoleDirection;
    const isLongCircuit = snapshotData ? snapshotData.isLongCircuit : fit.isLongCircuit;
    const pairedCylinderId = snapshotData ? snapshotData.pairedCylinderId : fit.pairedCylinderId;
    
    // Определяем родительский цилиндр через сварные швы
    let parentCylinderId = null;
    let weldX = null;
    let weldY = null;
    
    const isCircuitFitting = fit.id && (
      fit.id.includes('fitting-circuit-') || 
      fit.id.includes('fitting-bottom-circuit-') ||
      fit.id.startsWith('fitting-left-') ||      // левые подключения гидроразделителя
      fit.id.startsWith('fitting-right-')         // правые подключения гидроразделителя
    );
    
    // Для контурных фитингов и фитингов с явным igesParentCylinderId используем его, если он есть
    // Это надёжнее, чем сварные швы, потому что Canvas автоматически создаёт
    // сварные швы по пересечению, и длинные патрубки обратки приваривются к подающей трубе
    if (igesParentCylinderId) {
      parentCylinderId = igesParentCylinderId;
    } else {
      // Ищем сварной шов, который связывает этот фитинг с цилиндром
      const relatedWeld = welds.find(weld => {
        // Проверяем оба формата
        if (weld.objects && weld.objects.includes(fit.id)) {
          return true;
        }
        if (weld.obj1 && weld.obj1.objectId === fit.id) {
          return true;
        }
        if (weld.obj2 && weld.obj2.objectId === fit.id) {
          return true;
        }
        return false;
      });
      
      if (relatedWeld) {
        // Находим цилиндр в этом сварном шве
        let cylinderInWeld = null;
        
        if (relatedWeld.objects) {
          // Старый формат
          cylinderInWeld = relatedWeld.objects.find(objId => 
            cylinders.some(cyl => cyl.id === objId)
          );
        } else {
          // Новый формат (obj1/obj2)
          if (relatedWeld.obj1 && relatedWeld.obj1.objectId) {
            // Проверяем, является ли obj1 цилиндром
            if (cylinders.some(cyl => cyl.id === relatedWeld.obj1.objectId)) {
              cylinderInWeld = relatedWeld.obj1.objectId;
            }
          }
          if (!cylinderInWeld && relatedWeld.obj2 && relatedWeld.obj2.objectId) {
            // Проверяем, является ли obj2 цилиндром
            if (cylinders.some(cyl => cyl.id === relatedWeld.obj2.objectId)) {
              cylinderInWeld = relatedWeld.obj2.objectId;
            }
          }
        }
        
        if (cylinderInWeld) {
          parentCylinderId = cylinderInWeld;
          weldX = relatedWeld.x;
          weldY = relatedWeld.y;
        }
      }
      
      // ВАЖНО: Для контурных фитингов НЕ используем fallback поиск ближайшего цилиндра!
      // Если не нашли через сварные швы для обычных фитингов, пытаемся найти ближайший цилиндр
      if (!parentCylinderId && !isCircuitFitting) {
        const closestCylinder = findClosestCylinder(fit, cylinders);
        if (closestCylinder) {
          parentCylinderId = closestCylinder.id;
        }
      }
      
      if (isCircuitFitting) {
        // Для контурных фитингов используем найденного родителя
      }
    }
    
    // Определяем позицию фитинга (используем снимок если есть)
    const fitX = fitGroupX !== undefined ? fitGroupX : fit.x || 0;
    const fitY = fitGroupY !== undefined ? fitGroupY : fit.y || 0;
    
    // Для центральных фитингов (термометры, лицевые подключения) используем groupX,
    // для контурных фитингов тоже используем groupX (это координата сварного шва),
    // для остальных фитингов используем координаты сварного шва (если есть)
    let posX, posY;
    // isCircuitFitting уже объявлена выше
    
    if (fit.isCenterFitting || fit.isSelfFacing || isCircuitFitting) {
      // Центральные фитинги и контурные фитинги - всегда используем groupX/groupY (из снимка)
      // Для контурных фитингов groupX - это координата центра контура (сварного шва)
      posX = fitX;
      posY = fitY;
    } else {
      // Другие фитинги - используем координаты сварного шва, если они есть (приоритет!)
      posX = weldX !== null ? weldX : fitX;
      posY = weldY !== null ? weldY : fitY;
    }
    
    
    // Определяем диаметр отверстия (используем снимок если есть)
    let fittingHoleDiameter = 20; // значение по умолчанию
    
    if (holeDiameter) {
      fittingHoleDiameter = holeDiameter;
    } else if (fit.holeDiameter) {
      fittingHoleDiameter = fit.holeDiameter;
    } else if (fit.width) {
      fittingHoleDiameter = fit.width;
    } else if (fitDiameter) {
      fittingHoleDiameter = fitDiameter;
    } else if (fit.diameter) {
      fittingHoleDiameter = fit.diameter;
    }
    
    return {
      id: fit.id,
      parentCylinderId: parentCylinderId,
      x: posX,
      y: posY,
      z: fit.z || 0,
      holeDiameter: fittingHoleDiameter,
      angleX: fit.angleX || 0,
      angleY: fit.angleY || 0,
      type: fit.type || fit.key || fit.itemKey || 'unknown',
      // Добавляем информацию о том, является ли фитинг центральным
      isCenterFitting: fit.isCenterFitting || false,
      isSelfFacing: fit.isSelfFacing || false,
      side: fit.side || null, // Сторона для центральных фитингов: 'left', 'right', или null
      // IGES: флаг длинного патрубка (для создания 3 отверстий вместо 1) - из снимка если есть
      isLongCircuit: isLongCircuit || false,
      // IGES: флаг, что этот фитинг создаёт отверстие на основной трубе - из снимка если есть
      igesCreateHole: igesCreateHole !== undefined ? igesCreateHole : true,
      // IGES: явное направление отверстия ('up' или 'down'), если задано - из снимка если есть
      holeDirection: igesHoleDirection || null,
      // IGES: ID парного цилиндра для длинных патрубков - из снимка если есть
      pairedCylinderId: pairedCylinderId || null,
    };
  });
}

/**
 * Находит ближайший цилиндр к фитингу
 * @param {Object} fitting - фитинг
 * @param {Array} cylinders - массив цилиндров
 * @returns {Object|null}
 */
function findClosestCylinder(fitting, cylinders) {
  if (!cylinders || cylinders.length === 0) {
    return null;
  }
  
  let closestCylinder = null;
  let minDistance = Infinity;
  
  for (const cylinder of cylinders) {
    const dx = (fitting.x || 0) - (cylinder.x || 0);
    const dy = (fitting.y || 0) - (cylinder.y || 0);
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance < minDistance) {
      minDistance = distance;
      closestCylinder = cylinder;
    }
  }
  
  return closestCylinder;
}

/**
 * Генерирует имя файла из артикула и параметров трубы
 * Формат: Артикул-01(ф42.4х2, L=356, AISI304, Зеркало, шт.)
 * @param {Object} drawingData - данные чертежа
 * @param {Object} cylinder - конкретный цилиндр для экспорта
 * @param {number} pipeNumber - номер трубы (1, 2, 3...)
 * @returns {string}
 */
function generateFilename(drawingData, cylinder, pipeNumber = null) {
  try {
    // Получаем артикул из аннотаций
    const annotations = drawingData?.annotations || [];
    const artikulAnnotation = annotations.find(a => a.id === 'anno-init-1');
    const artikul = artikulAnnotation?.text?.trim() || 'Деталь';
    
    // Добавляем номер трубы после артикула, если передан
    const artikulWithNumber = pipeNumber ? `${artikul}-${String(pipeNumber).padStart(2, '0')}` : artikul;
    
    if (!cylinder) {
      return `${artikulWithNumber}.iges`;
    }
    
    // Определяем форму и размеры
    const isSquare = cylinder.shape === 'square';
    let sizeStr = '';
    
    if (isSquare) {
      // Квадратный профиль: "Профиль 60х60х2"
      const width = cylinder.outerWidth || 20;
      const height = cylinder.outerHeight || 20;
      const thickness = cylinder.thickness || 2;
      sizeStr = `Профиль ${width}х${height}х${thickness}`;
    } else {
      // Круглая труба: "ф42.4х2"
      const diameter = cylinder.outerDiameter || 42.4;
      const thickness = cylinder.thickness || 2;
      sizeStr = `ф${diameter}х${thickness}`;
    }
    
    // Длина трубы (с учётом insertionDepth для круглых труб коллектора)
    let length = cylinder.length || 0;
    
    // Для круглых труб с insertionDepth добавляем глубину вставки к длине
    // Это реальная длина трубы для производства
    if (cylinder.insertionDepth && cylinder.insertionDepth > 0) {
      length = length + cylinder.insertionDepth;
    }
    
    length = Math.round(length);
    
    // Материал (всегда AISI304 для нержавейки)
    const material = 'AISI304';
    
    // Покрытие берем из актуальной трубы (не из снимка!)
    const coating = cylinder?.coating || 'Матовый';
    
    // Количество из метаданных (workshopInfo.quantity)
    const quantityValue = drawingData?.metadata?.quantity || 1;
    const quantity = `${quantityValue}шт.`;
    
    // Формируем имя файла: Артикул-01(параметры)
    const filename = `${artikulWithNumber}(${sizeStr}, L=${length}, ${material}, ${coating}, ${quantity})`;
    
    // Очищаем от недопустимых символов для имени файла
    const safeName = filename.replace(/[<>:"/\\|?*]/g, '_');
    
    return `${safeName}.iges`;
  } catch (error) {
    // Fallback
    const date = new Date().toISOString().split('T')[0];
    return `model_${date}.iges`;
  }
}

function generateStepFilename(drawingData) {
  try {
    const annotations = drawingData?.annotations || [];
    const artikulAnnotation = annotations.find((a) => a.id === 'anno-init-1');
    const artikul = artikulAnnotation?.text?.trim() || 'Изделие';
    const safeName = artikul.replace(/[<>:"/\\|?*]/g, '_');
    return `${safeName}.stp`;
  } catch (error) {
    const date = new Date().toISOString().split('T')[0];
    return `model_${date}.stp`;
  }
}

/**
 * Один STEP на всё изделие: трубы и простые тела ниппелей, муфт, заглушек, бортшайб и фланцев.
 */
function buildStepRequestBody(drawingData) {
  const welds = drawingData.welds || [];
  const rawCylinders = drawingData.cylinders || [];
  const fittingsRaw = drawingData.fittings || [];
  const metadata = drawingData.metadata || {};
  stepSheetShift = sheetFitShift(metadata, rawCylinders);

  let cylinders = prepareCylindersData(rawCylinders, welds, metadata).map((cylinder) => {
    const raw = rawCylinders.find((item) => item.id === cylinder.id) || cylinder;
    return alignManualCylinder(cylinder, raw, metadata);
  });
  const fittings = prepareFittingsData(fittingsRaw, rawCylinders, welds, metadata)
    .map((fitting) => ({
      ...fitting,
      holeDiameter: stepRoundHoleDiameter(fitting.holeDiameter, fitting),
    }));
  const fittingBodies = prepareStepFittingBodies(fittingsRaw, metadata, rawCylinders);
  const fasteners = prepareStepFasteners(fittingsRaw);

  if (metadata.isCompact) {
    // Лист подачи остаётся в сборке: это П-образный профиль без нижней стенки.
    // Остальные тела с «supply» в имени по-прежнему не трубы изделия.
    cylinders = cylinders.filter((c) => !c.id?.includes('supply') || c.id.includes('supply-main'));
  }
  cylinders = cylinders.map((cylinder) => ({ ...cylinder, assemblyPlacement: true }));
  const stickOutShifts = new Map();
  sinkStepOutlets(
    fittingBodies,
    fittingsRaw,
    cylinders,
    rawCylinders,
    metadata,
    drawingData.dimensionLines,
    stickOutShifts,
  );
  pushNakShaibaOutletsToChangedValue(
    cylinders,
    rawCylinders,
    fittingBodies,
    fittingsRaw,
    welds,
    metadata,
    stickOutShifts,
  );
  followWeldedStickOut(
    cylinders,
    fittingBodies,
    fittingsRaw,
    welds,
    metadata,
    stickOutShifts,
  );
  markInsertionAtTouchingEnd(cylinders, rawCylinders, metadata);
  expandFacingAssemblies(fittingBodies, fittingsRaw, rawCylinders);
  punchEndCapHoles(fittingBodies, cylinders);
  return { cylinders, fittings, fittingBodies, fasteners };
}

function outletPipeDiameter(body) {
  const key = body.partKey || '';
  if (key.startsWith('Nippel_')) return Number(body.diameter);
  if (key.startsWith('Mufta_')) return Number(body.innerDiameter) || Number(body.diameter);
  if (key.startsWith('Flanec_')) {
    const dn = (key.match(/Flanec_(\d+)/) || [])[1];
    return Number(DN_TO_PIPE_DIAMETER[dn]);
  }
  if (key.startsWith('Nak_Gaika_')) {
    return Number(NAK_GAIKA_TO_PIPE_DIAMETER[key.slice('Nak_Gaika_'.length)]);
  }
  if (key.startsWith('Shaib_Nar_Rezba_')) {
    return Number(SHAIBA_NAR_REZBA_TO_PIPE_DIAMETER[key.slice('Shaib_Nar_Rezba_'.length)]);
  }
  return 0;
}

function cylinderAxisEnds(cylinder) {
  const startX = Number(cylinder.metadata?.chartStartX) || 0;
  const startY = Number(cylinder.metadata?.chartStartY) || 0;
  const length = Number(cylinder.length) || 0;
  if (cylinder.orientation === 'vertical') {
    const x = startX + (Number(cylinder.bboxWidth) || Number(cylinder.outerDiameter) || 0) / 2;
    return [[x, startY], [x, startY + length]];
  }
  const y = Number(cylinder.y) || 0;
  return [[startX, y], [startX + length, y]];
}

function punchEndCapHoles(bodies, cylinders) {
  const outlets = [];
  (bodies || []).forEach((body) => {
    const size = outletPipeDiameter(body);
    if (!(size > 2)) return;
    outlets.push({
      size,
      ax: body.tipX - body.baseX,
      ay: body.tipY - body.baseY,
      ends: [[body.baseX, body.baseY], [body.tipX, body.tipY]],
    });
  });
  (cylinders || []).forEach((cylinder) => {
    if (cylinder.shape !== 'round') return;
    const id = String(cylinder.id || '');
    if (!id.includes('intermediate')) return;
    const size = Number(cylinder.outerDiameter);
    if (!(size > 2)) return;
    const ends = cylinderAxisEnds(cylinder);
    outlets.push({
      size,
      ax: ends[1][0] - ends[0][0],
      ay: ends[1][1] - ends[0][1],
      ends,
    });
  });
  (bodies || []).forEach((cap) => {
    const key = cap.partKey || '';
    if (!key.startsWith('Zaglushka_')) return;
    const ax = cap.tipX - cap.baseX;
    const ay = cap.tipY - cap.baseY;
    const capLength = Math.hypot(ax, ay) || 1;
    const ux = ax / capLength;
    const uy = ay / capLength;
    const capEnds = [[cap.tipX, cap.tipY], [cap.baseX, cap.baseY]];
    let best = null;
    outlets.forEach((outlet) => {
      const outletLength = Math.hypot(outlet.ax, outlet.ay) || 1;
      const parallel = Math.abs((outlet.ax / outletLength) * ux + (outlet.ay / outletLength) * uy);
      if (parallel < 0.85) return;
      const near = capEnds.reduce((smallest, [x, y]) => Math.min(
        smallest,
        ...outlet.ends.map(([ex, ey]) => Math.hypot(x - ex, y - ey)),
      ), Infinity);
      if (near > 20) return;
      if (!best || near < best.near) best = { near, size: outlet.size };
    });
    if (!best) return;
    const capSize = Math.min(
      Number(cap.sideWidth) || Number(cap.diameter) || 0,
      Number(cap.sideHeight) || Number(cap.diameter) || 0,
    );
    if (!(capSize > best.size + 4)) return;
    cap.capHole = best.size;
  });
}

export async function projectFastenerDrawing({ partKey, clampDiameter, axialDistance, overhang, height, rangeOffset }) {
  const isBackendAvailable = await checkBackendHealth();
  if (!isBackendAvailable) {
    throw new Error('Сервер 3D-модели не запущен. Чертёж крепежа строится из той же модели, что и STEP.');
  }
  const response = await fetch(`${BACKEND_URL}/fastener-drawing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      partKey,
      clampDiameter,
      axialDistance,
      overhang,
      height,
      rangeOffset,
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || payload.error || `Ошибка сервера: ${response.status}`);
  }
  return payload;
}

export async function projectTopView(drawingData, kind = 'top') {
  const isBackendAvailable = await checkBackendHealth();
  if (!isBackendAvailable) {
    throw new Error('Сервер 3D-модели не запущен. Вид сверху строится из той же модели, что и STEP.');
  }
  const { cylinders, fittings, fittingBodies, fasteners } = buildStepRequestBody(drawingData);
  if (cylinders.length === 0) {
    throw new Error('Нет труб для вида сверху.');
  }
  const response = await fetch(`${BACKEND_URL}/project-view/${kind}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      cylinders,
      fittings,
      fittingBodies,
      fasteners,
      assembly: true,
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || payload.error || `Ошибка сервера: ${response.status}`);
  }
  return { polylines: payload.polylines || [], groups: payload.groups || [] };
}

const PREVIEW_MESH_REVISION = 3;
const previewMeshCache = { key: '', payload: null };

export async function fetchPreviewMesh(drawingData) {
  const { cylinders, fittings, fittingBodies, fasteners } = buildStepRequestBody(drawingData);
  if (cylinders.length === 0) {
    throw new Error('Нет труб для просмотра. Добавьте изделие на чертёж.');
  }
  const cacheKey = JSON.stringify({
    revision: PREVIEW_MESH_REVISION,
    cylinders,
    fittings,
    fittingBodies,
    fasteners,
  });
  if (previewMeshCache.key === cacheKey && previewMeshCache.payload) {
    return previewMeshCache.payload;
  }
  const isBackendAvailable = await checkBackendHealth();
  if (!isBackendAvailable) {
    throw new Error('Сервер 3D-модели не запущен. Просмотр строится из той же сборки, что и STEP.');
  }
  const response = await fetch(`${BACKEND_URL}/preview-mesh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      cylinders,
      fittings,
      fittingBodies,
      fasteners,
      assembly: true,
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.message || payload.error || `Ошибка сервера: ${response.status}`);
  }
  const groups = (payload.groups || []).filter((group) => group.positions?.length && group.indices?.length);
  const mesh = groups.length
    ? { groups }
    : (payload.positions?.length && payload.indices?.length
      ? {
        groups: [{
          material: 'steel',
          positions: payload.positions,
          normals: payload.normals,
          indices: payload.indices,
        }],
      }
      : null);
  if (!mesh) {
    throw new Error('Сервер вернул пустую модель.');
  }
  previewMeshCache.key = cacheKey;
  previewMeshCache.payload = mesh;
  return mesh;
}

export async function exportToSTEP(drawingData, filename = null, saveContext = {}) {
  const { directoryHandle = null, fileHandle = null } = saveContext;
  try {
    if (!drawingData) {
      throw new Error('Нет данных для экспорта');
    }

    const isBackendAvailable = await checkBackendHealth();
    if (!isBackendAvailable) {
      throw new Error(
        'Backend сервер недоступен.\n\n' +
        'Убедитесь, что Python сервер запущен:\n' +
        'python backend/iges_generator.py'
      );
    }

    const { cylinders, fittings, fittingBodies, fasteners } = buildStepRequestBody(drawingData);

    if (cylinders.length === 0) {
      throw new Error('Нет труб для экспорта. Добавьте цилиндры на чертеж.');
    }

    const outputFilename = filename || generateStepFilename(drawingData);
    const response = await fetch(`${BACKEND_URL}/generate-step`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cylinders,
        fittings,
        fittingBodies,
        fasteners,
        filename: outputFilename,
        format: 'step',
        assembly: true,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
        errorData.error ||
        `Ошибка сервера: ${response.status}`
      );
    }

    const blob = await response.blob();
    await saveBlobAsFile(
      blob,
      outputFilename,
      'STEP файл',
      STEP_ACCEPT,
      directoryHandle,
      fileHandle,
    );
    return { success: true, directoryHandle };
  } catch (error) {
    console.error('Ошибка экспорта в STEP:', error);
    alert(
      'Ошибка экспорта в STEP\n\n' +
      error.message +
      '\n\nПроверьте консоль для подробностей.'
    );
    return { success: false, directoryHandle: null };
  }
}

export async function exportCurrentDrawingToSTEP(saveDrawingRef, saveContext = {}) {
  if (!saveDrawingRef || typeof saveDrawingRef !== 'function') {
    alert('Ошибка: не удалось получить данные чертежа');
    return { success: false, directoryHandle: null };
  }
  try {
    const drawingData = saveDrawingRef();
    if (!drawingData || !drawingData.canvas) {
      throw new Error('Нет данных чертежа для экспорта');
    }
    return await exportToSTEP(drawingData.canvas, null, saveContext);
  } catch (error) {
    console.error('Ошибка при экспорте STEP:', error);
    alert('Ошибка при экспорте чертежа: ' + error.message);
    return { success: false, directoryHandle: null };
  }
}

/**
 * Экспортирует чертеж в формат IGES
 * @param {Object} drawingData - данные чертежа из Canvas
 * @param {string} filename - имя файла (опционально)
 * @returns {Promise<boolean>} - true если успешно
 */
export async function exportToIGES(drawingData, filename = null, saveContext = {}) {
  const { directoryHandle = null, fileHandle = null } = saveContext;
  try {
    // Проверяем наличие данных
    if (!drawingData) {
      throw new Error('Нет данных для экспорта');
    }
    
    // Проверяем доступность backend
    const isBackendAvailable = await checkBackendHealth();
    
    if (!isBackendAvailable) {
      throw new Error(
        'Backend сервер недоступен.\n\n' +
        'Убедитесь, что Python сервер запущен:\n' +
        'python backend/iges_generator.py'
      );
    }
    
    // Подготавливаем данные
    const welds = drawingData.welds || [];
    const rawCylinders = drawingData.cylinders || [];
    const fittingsRaw = drawingData.fittings || [];
    const metadata = drawingData.metadata || {};
    
    const cylinders = prepareCylindersData(rawCylinders, welds, metadata);
    const fittings = prepareFittingsData(
      fittingsRaw,
      rawCylinders,
      welds,
      metadata
    );
    
    if (cylinders.length === 0) {
      throw new Error('Нет труб для экспорта. Добавьте цилиндры на чертеж.');
    }
    
    // Фильтруем основные трубы (не промежуточные и не контурные трубки от фланцев/гаек/шайб)
    const mainCylinders = filterMainCylinders(cylinders);
    
    // Если одна основная труба - создаём один файл
    if (mainCylinders.length === 1) {
      const outputFilename = filename || generateFilename(drawingData, mainCylinders[0], null);
      
      // Формируем запрос
      const requestData = {
        cylinders: cylinders,
        fittings: fittings,
        filename: outputFilename,
      };
      
      // Отправляем запрос на backend
      const response = await fetch(`${BACKEND_URL}/generate-iges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          errorData.error || 
          `Ошибка сервера: ${response.status}`
        );
      }
      
      // Получаем файл как blob
      const blob = await response.blob();
      
      // Сохраняем файл
      await saveBlobAsFile(
        blob,
        outputFilename,
        'IGES файл',
        IGES_ACCEPT,
        directoryHandle,
        fileHandle,
      );

      return { success: true, directoryHandle };
    } else {
      // Если несколько основных труб - создаём отдельный файл для каждой
      
      // Проверяем, компактный ли это режим
      const metadata = drawingData.metadata || {};
      const isCompact = metadata.isCompact || false;
      
      for (let i = 0; i < mainCylinders.length; i++) {
        const mainCylinder = mainCylinders[i];
        const pipeNumber = i + 1; // Общая нумерация для DXF и IGES
        
        // Для распределительного коллектора находим все вертикальные патрубки контуров
        // Они имеют ID типа 'cyl-circuit-supply-...' или 'cyl-circuit-return-...'
        const isSupply = mainCylinder.id.includes('supply');
        const isReturn = mainCylinder.id.includes('return');
        
        // В компактном режиме: подающий = лист (только DXF), обратный = профиль (IGES)
        if (isCompact && isSupply) {
          console.log('  ⏭️ ПРОПУЩЕНО: подающий коллектор (лист) экспортируется в DXF как Артикул-01, не в IGES');
          continue; // Пропускаем подающий, он будет в DXF как -01
        }
        
        // Используем pipeNumber для нумерации (supply=1 пропущен в DXF, return=2 будет в IGES как -02)
        
        // Для распределительного коллектора нужно включить:
        // 1. Основную трубу (supply или return)
        // 2. Все вертикальные трубы контуров, связанные с этой основной трубой
        // 3. Все фитинги, связанные с основной трубой и её контурами
        
        const relatedCylinders = [mainCylinder];
        const relatedCylinderIds = new Set([mainCylinder.id]);
        
        cylinders.forEach(c => {
          // Проверяем, относится ли этот цилиндр к текущему коллектору
          const isCircuitCylinder = c.id.includes('circuit');
          const belongsToSupply = c.id.includes('supply');
          const belongsToReturn = c.id.includes('return');
          
          if (isCircuitCylinder && 
              ((isSupply && belongsToSupply) || (isReturn && belongsToReturn)) &&
              c.id !== mainCylinder.id) {
            relatedCylinders.push(c);
            relatedCylinderIds.add(c.id);
          }
        });
        
        // Фильтруем фитинги для этой трубы и всех связанных контуров
        // ВАЖНО: Для распределительного коллектора включаем ВСЕ контурные фитинги,
        // потому что длинные патрубки создают отверстия на обеих трубах
        let pipeFittings;
        if (isSupply || isReturn) {
          // Для распределительного коллектора: отправляем фитинги этой трубы и контуров
          // НО исключаем центральные фитинги другой трубы (supply/return)
          // И исключаем торцевые подключения (fit-...-conn-)
          pipeFittings = fittings.filter(f => {
            // Исключаем торцевые подключения - они не создают отверстия
            if (f.id && (f.id.includes('-conn-') && (f.id.startsWith('fit-supply-') || f.id.startsWith('fit-return-')))) {
              return false;
            }
            // Исключаем центральные фитинги другой трубы
            if (f.id && f.id.includes('center-fitting-')) {
              // Проверяем, что родитель совпадает с текущей трубой
              return f.parentCylinderId === mainCylinder.id;
            }
            // Для контурных фитингов - оставляем все (для длинных патрубков)
            return true;
          });
        } else {
          // Для обычных коллекторов: только фитинги связанных труб
          pipeFittings = fittings.filter(f => relatedCylinderIds.has(f.parentCylinderId));
        }
        
        const outputFilename = filename || generateFilename(drawingData, mainCylinder, pipeNumber);
        
        // Формируем запрос для одной трубы со всеми её контурами
        // Для распределительного коллектора передаём ВСЕ трубы, чтобы бэкенд мог
        // создавать отверстия на парных трубах, но указываем mainCylinderId для экспорта
        const requestData = {
          cylinders: isSupply || isReturn ? mainCylinders : relatedCylinders,
          fittings: pipeFittings,
          filename: outputFilename,
          mainCylinderId: mainCylinder.id, // Указываем, какую трубу экспортировать
        };
        
        // Отправляем запрос на backend
        const response = await fetch(`${BACKEND_URL}/generate-iges`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || 
            errorData.error || 
            `Ошибка сервера: ${response.status}`
          );
        }
        
        // Получаем файл как blob
        const blob = await response.blob();
        
        // Сохраняем файл
        await saveBlobAsFile(
          blob,
          outputFilename,
          'IGES файл',
          IGES_ACCEPT,
          directoryHandle,
          null,
        );
      }

      return { success: true, directoryHandle };
    }
    
  } catch (error) {
    console.error('❌ Ошибка экспорта в IGES:', error);
    
    // Показываем пользователю понятное сообщение об ошибке
    alert(
      '❌ Ошибка экспорта в IGES\n\n' +
      error.message +
      '\n\nПроверьте консоль для подробностей.'
    );
    
    return { success: false, directoryHandle: null };
  }
}

/**
 * Экспортирует текущий чертеж в IGES (для использования в компонентах)
 * @param {Function} saveDrawingRef - функция получения данных чертежа
 * @returns {Promise<boolean>}
 */
export async function exportCurrentDrawingToIGES(saveDrawingRef, saveContext = {}) {
  if (!saveDrawingRef || typeof saveDrawingRef !== 'function') {
    console.error('saveDrawingRef не является функцией');
    alert('Ошибка: не удалось получить данные чертежа');
    return { success: false, directoryHandle: null };
  }
  
  try {
    const drawingData = saveDrawingRef();
    
    if (!drawingData || !drawingData.canvas) {
      throw new Error('Нет данных чертежа для экспорта');
    }
    
    // Metadata находится внутри canvas, просто передаём canvas напрямую
    // canvas уже содержит все необходимые данные включая metadata
    return await exportToIGES(drawingData.canvas, null, saveContext);

  } catch (error) {
    console.error('Ошибка при экспорте:', error);
    alert('Ошибка при экспорте чертежа: ' + error.message);
    return { success: false, directoryHandle: null };
  }
}

/**
 * Генерирует имя файла для DXF раскроя листа
 * Формат: Артикул-01(341х112.3, Лист AISI304(2мм), Зеркало, 1шт.).dxf
 * @param {Object} drawingData - данные чертежа
 * @param {Object} sheetData - данные листа {length, width, thickness}
 * @param {number} pipeNumber - номер файла (обычно 1 для листа)
 * @returns {string}
 */
function generateDXFFilename(drawingData, sheetData, pipeNumber = 1) {
  try {
    // Получаем артикул из аннотаций
    const annotations = drawingData?.annotations || [];
    const artikulAnnotation = annotations.find(a => a.id === 'anno-init-1');
    const artikul = artikulAnnotation?.text?.trim() || 'Деталь';
    
    // Добавляем номер после артикула
    const artikulWithNumber = `${artikul}-${String(pipeNumber).padStart(2, '0')}`;
    
    // Размеры: "341х112.3" (БЕЗ "L=")
    const length = Math.round(sheetData.length || 0);
    const width = sheetData.width || 112.3;
    const sizeStr = `${length}х${width}`;
    
    // Толщина для названия: "Лист AISI304(2мм)"
    const thickness = sheetData.thickness || 2;
    const thicknessStr = `${thickness}мм`;
    
    // Материал
    const material = 'AISI304';
    
    // Покрытие из актуального листа (не из снимка!)
    const coating = sheetData?.coating || 'Матовый';
    
    // Количество из метаданных (workshopInfo.quantity)
    const quantityValue = drawingData?.metadata?.quantity || 1;
    const quantity = `${quantityValue}шт.`;
    
    // Формируем имя: Артикул-01(341х112.3, Лист AISI304(2мм), Зеркало, 1шт.)
    const filename = `${artikulWithNumber}(${sizeStr}, Лист ${material}(${thicknessStr}), ${coating}, ${quantity})`;
    
    // Очищаем от недопустимых символов
    const safeName = filename.replace(/[<>:"/\\|?*]/g, '_');
    
    return `${safeName}.dxf`;
  } catch (error) {
    const date = new Date().toISOString().split('T')[0];
    return `sheet_${date}.dxf`;
  }
}

/**
 * Экспортирует лист в DXF для раскроя (для компактного коллектора)
 * @param {Object} drawingData - данные чертежа из Canvas
 * @returns {Promise<boolean>}
 */
export async function exportToDXF(drawingData, saveContext = {}) {
  const { directoryHandle = null, fileHandle = null } = saveContext;
  try {
    console.log('🔷 Начало экспорта в DXF...');
    
    // Проверяем доступность backend
    const backendAvailable = await checkBackendHealth();
    if (!backendAvailable) {
      throw new Error(
        'Backend сервер недоступен.\n\n' +
        'Убедитесь, что Python сервер запущен:\n' +
        'cd backend\n' +
        'python iges_generator.py'
      );
    }
    
    // Проверяем, что это компактный коллектор
    const metadata = drawingData.metadata || {};
    const isCompact = metadata.isCompact || false;
    const compactSize = metadata.compactSize || '80x80';
    
    if (!isCompact) {
      // Тихо выходим, если это не компактный коллектор
      console.log('ℹ️ DXF экспорт доступен только для компактного коллектора');
      return false;
    }
    
    console.log('✅ Это компактный коллектор:', compactSize);
    
    // Определяем ширину листа в зависимости от размера
    const sheetWidth = compactSize === '60x60' ? 112.3 : 152.3;
    
    // Получаем данные цилиндров и фитингов
    const cylinders = drawingData.cylinders || [];
    const fittings = drawingData.fittings || [];
    
    // Ищем подающий коллектор (он является листом в компактном режиме)
    // Обычно это первая труба или труба с id содержащим 'supply'
    const supplyPipe = cylinders.find(c => 
      c.id?.includes('supply') || c.id?.includes('подающ')
    ) || cylinders[0];
    
    if (!supplyPipe) {
      throw new Error('Не найден подающий коллектор (лист) для экспорта');
    }
    
    console.log('📐 Найден подающий коллектор (лист):', supplyPipe.id);
    
    // Получаем снимок данных (если есть) или используем текущие
    const igesSnapshot = metadata.igesSnapshot || {};
    const snapshotCylinders = igesSnapshot.cylinders || [];
    const snapshotFittings = igesSnapshot.fittings || [];
    
    // Находим снимок подающей трубы
    const supplySnapshot = snapshotCylinders.find(s => s.id === supplyPipe.id);
    
    // Длина листа = длина подающей трубы
    const sheetLength = supplySnapshot ? supplySnapshot.width : supplyPipe.width;
    
    // ВАЖНО: Начальная X-координата трубы (левый край)
    // Отверстия должны быть относительно начала ТРУБЫ, а не всего чертежа
    const pipeStartX = supplySnapshot ? supplySnapshot.x : supplyPipe.x;
    
    console.log(`📏 Труба: startX=${pipeStartX.toFixed(1)}, length=${sheetLength.toFixed(1)}`);
    
    // Толщина из pipeSpec
    const thickness = supplyPipe.pipeSpec?.thickness || 2;
    
    // Покрытие из актуального pipeSpec (не из снимка!)
    const coating = supplyPipe.pipeSpec?.coating || 'Матовый';
    
    // Собираем отверстия
    // Отверстия находятся на подающей трубе - это контурные фитинги и центральные
    const supplyFittings = fittings.filter(f => {
      const parentId = f.igesParentCylinderId || f.parentCylinderId;
      return parentId === supplyPipe.id;
    });
    
    console.log(`🔘 Найдено фитингов на листе: ${supplyFittings.length}`);
    
    // Разделяем на контурные и центральные
    const circuitFittings = supplyFittings.filter(f => !f.isCenterFitting);
    const centerFittings = supplyFittings.filter(f => f.isCenterFitting);
    
    console.log(`  📍 Контурных: ${circuitFittings.length}, Центральных: ${centerFittings.length}`);
    
    // Преобразуем фитинги в отверстия для DXF
    const holes = [];
    
    // 1. КОНТУРНЫЕ ФИТИНГИ (вдоль трубы)
    for (const fit of circuitFittings) {
      // Используем снимок если есть
      let fitSnapshot = null;
      if (igesSnapshot.fittings) {
        fitSnapshot = snapshotFittings.find(s => s.id === fit.id);
      }
      
      // Абсолютная координата фитинга на чертеже
      const fitAbsoluteX = fitSnapshot ? fitSnapshot.groupX : (fit.groupX || fit.x);
      
      // ВАЖНО: Преобразуем в относительную координату от начала трубы
      const fitRelativeX = fitAbsoluteX - pipeStartX;
      
      // ВАЖНО: Для DXF нужен реальный диаметр отверстия (диаметр трубы контура),
      // а не размер фитинга на чертеже!
      // Приоритет: holeDiameter (явно заданный) > реальный диаметр из pipeSpec
      let holeDiameter = 20; // По умолчанию
      
      if (fitSnapshot && fitSnapshot.holeDiameter) {
        // Из снимка
        holeDiameter = fitSnapshot.holeDiameter;
      } else if (fit.holeDiameter) {
        // Явно заданный диаметр отверстия
        holeDiameter = fit.holeDiameter;
      } else if (fit.pipeSpec && fit.pipeSpec.outerDiameter) {
        // Из спецификации трубы контура
        holeDiameter = fit.pipeSpec.outerDiameter;
      } else if (fit.realDiameter) {
        // Реальный диаметр (если есть)
        holeDiameter = fit.realDiameter;
      }
      
      holes.push({
        x: fitRelativeX,  // Относительная координата от начала трубы
        y: sheetWidth / 2, // По центру ширины листа
        diameter: holeDiameter,
        type: 'circuit', // Тип отверстия
      });
      
      console.log(`  - Контурное: X=${fitRelativeX.toFixed(1)} мм (от начала трубы), Y=${(sheetWidth/2).toFixed(1)} мм, диаметр=${holeDiameter} мм (fit.id=${fit.id})`);
    }
    
    // 2. ЦЕНТРАЛЬНЫЕ ФИТИНГИ (спереди/сзади, с 2-х сторон)
    // Определяем отступ от стенки в зависимости от размера
    const centerOffset = compactSize === '60x60' ? 15 : 20;
    
    for (const fit of centerFittings) {
      // Используем снимок если есть
      let fitSnapshot = null;
      if (igesSnapshot.fittings) {
        fitSnapshot = snapshotFittings.find(s => s.id === fit.id);
      }
      
      // Абсолютная координата фитинга на чертеже
      const fitAbsoluteX = fitSnapshot ? fitSnapshot.groupX : (fit.groupX || fit.x);
      const fitRelativeX = fitAbsoluteX - pipeStartX;
      
      // Диаметр отверстия для сварки
      let holeDiameter = 20;
      if (fitSnapshot && fitSnapshot.holeDiameter) {
        // Снимок уже содержит готовый holeDiameter — используем напрямую
        holeDiameter = fitSnapshot.holeDiameter;
      } else if (fit.holeDiameter) {
        // Явно заданный готовый диаметр отверстия
        holeDiameter = fit.holeDiameter;
      } else if (fit.realDiameter) {
        holeDiameter = getHoleDiameterForPipe(fit.realDiameter, { isSquare: true });
      } else if (fit.width) {
        holeDiameter = getHoleDiameterForPipe(fit.width, { isSquare: true });
      }
      
      // Y-координата зависит от стороны
      let yCoord;
      const side = fit.side || 'front';
      
      if (side === 'front') {
        // Спереди: от нижней стенки
        yCoord = centerOffset;
      } else if (side === 'back') {
        // Сзади: от верхней стенки
        yCoord = sheetWidth - centerOffset;
      } else {
        // Неизвестная сторона - по центру
        yCoord = sheetWidth / 2;
      }
      
      holes.push({
        x: fitRelativeX,
        y: yCoord,
        diameter: holeDiameter,
        type: 'center',
      });
      
      console.log(`  - Центральное (${side}): X=${fitRelativeX.toFixed(1)} мм, Y=${yCoord.toFixed(1)} мм (отступ ${centerOffset}мм от ${side === 'front' ? 'нижней' : 'верхней'} стенки), диаметр=${holeDiameter} мм (fit.id=${fit.id})`);
    }
    
    // Формируем данные для отправки на backend
    const sheetData = {
      length: sheetLength,
      width: sheetWidth,
      thickness: thickness,
      coating: coating, // Актуальное покрытие
    };
    
    // Генерируем имя файла
    const filename = generateDXFFilename(drawingData, sheetData, 1);
    
    console.log('📄 Имя файла:', filename);
    console.log('📦 Размеры листа:', `${sheetLength} x ${sheetWidth} мм, толщина ${thickness} мм`);
    
    const requestData = {
      length: sheetLength,
      width: sheetWidth,
      holes: holes,
      filename: filename,
    };
    
    // Отправляем запрос на backend
    const response = await fetch(`${BACKEND_URL}/generate-dxf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || 
        errorData.error || 
        `Ошибка сервера: ${response.status}`
      );
    }
    
    // Получаем файл как blob
    const blob = await response.blob();
    
    // Сохраняем файл
    await saveBlobAsFile(
      blob,
      filename,
      'DXF файл',
      DXF_ACCEPT,
      directoryHandle,
      fileHandle,
    );
    
    console.log('✅ DXF файл успешно экспортирован');
    
    return true;
    
  } catch (error) {
    console.error('❌ Ошибка экспорта в DXF:', error);
    
    alert(
      '❌ Ошибка экспорта в DXF\n\n' +
      error.message +
      '\n\nПроверьте консоль для подробностей.'
    );
    
    return false;
  }
}

/**
 * Экспортирует текущий чертеж в DXF (для использования в компонентах)
 * @param {Function} saveDrawingRef - функция получения данных чертежа
 * @returns {Promise<boolean>}
 */
export async function exportCurrentDrawingToDXF(saveDrawingRef, saveContext = {}) {
  if (!saveDrawingRef || typeof saveDrawingRef !== 'function') {
    console.error('saveDrawingRef не является функцией');
    alert('Ошибка: не удалось получить данные чертежа');
    return false;
  }
  
  try {
    const drawingData = saveDrawingRef();
    
    if (!drawingData || !drawingData.canvas) {
      throw new Error('Нет данных чертежа для экспорта');
    }
    
    return await exportToDXF(drawingData.canvas, saveContext);

  } catch (error) {
    console.error('Ошибка при экспорте DXF:', error);
    alert('Ошибка при экспорте чертежа в DXF: ' + error.message);
    return false;
  }
}

// Экспорт по умолчанию
const igesExporter = {
  exportToIGES,
  exportCurrentDrawingToIGES,
  exportToSTEP,
  exportCurrentDrawingToSTEP,
  exportToDXF,
  exportCurrentDrawingToDXF,
  checkBackendHealth,
};

export default igesExporter;
